import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Header from "../components/header";
import Main from "../components/main";
import ApiService from "../api/apiService";
import Websocket from "../hooks/ws";
import { WebSocketContext, ChatContext } from "../context/contexts";
import showSwal from "../components/swal";
import { common } from "../util/common";
import notifSound from "../sounds/notif-sound.mp3";
import showDesktopNotification from "../hooks/user-notification";

function ChatIndex(props) {
  const { userDetails, linkConversation } = props;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLoggedIn, setLoggedIn] = useState(false);
  const [viewChangePasswordModal, setViewChangePasswordModal] = useState(false);

  const [attachedListener, setAttachedListener] = useState(false);

  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [storedConversationId, setConversationId] = useState(null);
  const [conversationData, setConversationData] = useState(null);
  const [conversationUserPermission, setConversationUserPermission] =
    useState(null);
  const [isPermittedToChat, setIsPermittedToChat] = useState(true);
  const [conversationMembersData, setConversationMembers] = useState([]);
  const [messagesData, setMessagesData] = useState(null);

  const [wsMessagesData, setWsMessagesData] = useState([]);

  const [wsDeleteActionsMessageId, setWsDeleteActionsMessageId] =
    useState(null);

  const [wsTheradMsgs, setWsThreadMsgs] = useState(null);

  const [wsReactionMessageId, setWsReactionMessageId] = useState(null);
  const [wsDeletedReactionMessageId, setWsDeletedReactionMessageId] =
    useState(null);
  const [wsReactionMessageDetail, setWsReactionMessageDetail] = useState(null);

  const [hasSelectedChat, setHasSelectedChat] = useState(false);
  const [isMobileMode, setMobileMode] = useState(false);
  const [isMobileChatDetailShow, setMobileChatDetailShow] = useState(false);
  const [chatReplyData, setChatReplyData] = useState(null);
  const messageMainRef = useRef(null);
  const isLoadingMoreRef = useRef(false);
  const isAtBottomRef = useRef(true);
  // Set when returning to the tab while the thread was at the bottom on hide;
  // consumed by the next resync so merged messages don't leave it mid-thread.
  const stickToBottomOnSyncRef = useRef(false);
  const activeConversationIdRef = useRef(null);

  const [threadViewMsgId, setThreadViewMsgId] = useState(null);
  const [threadViewVisible, setThreadViewVisible] = useState(false);
  const [reactionsViewVisible, setReactionsViewVisible] = useState(false);
  const [reactionViewList, setReactionViewList] = useState([]);
  const [threadViewDetails, setThreadViewDetails] = useState(null);
  const [storedThreadMessageId, setStoredThreadMessageId] = useState(null);

  const [onlineUsers, setOnlineUsers] = useState([]);

  const [isShowingTyping, setShowingTyping] = useState(false);
  const [typingData, setTypingData] = useState(null);

  const [seenMessageData, setSeenMessageData] = useState(null);

  const [pinMessageData, setPinMessageData] = useState(null);

  const [conversationLimit, setConversationLimit] = useState(9);
  const [conversationFlag, setConversationFlag] = useState("personal");
  const [personalUnreadCount, setPersonalUnreadCount] = useState(0);
  const [groupUnreadCount, setGroupUnreadCount] = useState(0);
  const websocketInstance = useRef(new Websocket()).current;
  const conversationCacheRef = useRef(new Map());
  const CONVERSATION_CACHE_TTL_MS = 60 * 1000;
  const [wsConnectionState, setWsConnectionState] = useState(
    websocketInstance.getConnectionState?.() || {
      status: "disconnected",
      attempt: 0,
      reason: null,
      roomId: null,
    },
  );
  const [isRealtimeSyncing, setIsRealtimeSyncing] = useState(false);
  const previousWsStatusRef = useRef(wsConnectionState.status);
  const conversationFlagRef = useRef(conversationFlag);
  const conversationLimitRef = useRef(conversationLimit);
  const messagesDataRef = useRef(messagesData);
  const wsMessagesDataRef = useRef(wsMessagesData);
  const fetchConversationsRef = useRef(null);
  const listRefetchDebounceRef = useRef(null);
  const conversationListListenerAttachedRef = useRef(false);
  const dataRef = useRef(data);
  const realtimeVersionRef = useRef(0);
  // Update the ref synchronously: back-to-back socket events (different
  // conversations) otherwise each build on the same stale snapshot, and the
  // second setData silently drops the first update.
  const commitData = useCallback((next) => {
    dataRef.current = next;
    setData(next);
  }, []);
  const handleConversationUpdatedRef = useRef(null);

  const hasValidLinkConversation =
    linkConversation !== null &&
    linkConversation !== undefined &&
    String(linkConversation).trim() !== "";

  useEffect(() => {
    conversationFlagRef.current = conversationFlag;
  }, [conversationFlag]);

  useEffect(() => {
    conversationLimitRef.current = conversationLimit;
  }, [conversationLimit]);

  useEffect(() => {
    messagesDataRef.current = messagesData;
  }, [messagesData]);

  useEffect(() => {
    wsMessagesDataRef.current = wsMessagesData;
  }, [wsMessagesData]);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    if (userDetails.id && userDetails.display_name) {
      setLoggedIn(true);
      websocketInstance.initConnection(
        userDetails.id,
        userDetails.display_name,
      );
      websocketInstance.indicateOnline(
        userDetails.id,
        userDetails.display_name,
        (callback) => {
          setOnlineUsers(callback);
        },
      );
      websocketInstance.initSidebarRooms();
    } else {
      setLoggedIn(false);
    }
  }, [userDetails]);

  useEffect(() => {
    if (!isLoggedIn) return;

    websocketInstance.setupConversationListListener((payload) => {
      if (typeof handleConversationUpdatedRef.current === "function") {
        handleConversationUpdatedRef.current(payload);
      }
    });
    conversationListListenerAttachedRef.current = true;
    websocketInstance.initSidebarRooms();
  }, [isLoggedIn, websocketInstance]);

  useEffect(() => {
    if (!isLoggedIn) return;

    let isCancelled = false;
    let pollTimeoutId;
    let retryTimeoutId;
    let retryAttempt = 0;

    const FALLBACK_POLL_INTERVAL_MS = 5 * 60 * 1000;
    const MAX_RETRY_ATTEMPTS = 3;
    const BASE_RETRY_DELAY_MS = 3000;

    const scheduleNextPoll = (delay = FALLBACK_POLL_INTERVAL_MS) => {
      clearTimeout(pollTimeoutId);
      if (!isCancelled) {
        pollTimeoutId = setTimeout(() => {
          fetchConversationData();
        }, delay);
      }
    };

    const scheduleRetry = () => {
      if (retryAttempt >= MAX_RETRY_ATTEMPTS) {
        retryAttempt = 0;
        scheduleNextPoll();
        return;
      }

      const retryDelay = Math.min(
        BASE_RETRY_DELAY_MS * 2 ** retryAttempt,
        30000,
      );
      retryAttempt += 1;

      clearTimeout(retryTimeoutId);
      if (!isCancelled) {
        retryTimeoutId = setTimeout(() => {
          fetchConversationData();
        }, retryDelay);
      }
    };

    const fetchConversationData = async () => {
      try {
        const versionAtStart = realtimeVersionRef.current;
        const result =
          await ApiService.v3.conversations.getConversationByUserWithFlag(
            conversationFlag,
            1,
            conversationLimit,
          );

        if (isCancelled) return;

        commitData(result);
        retryAttempt = 0;
        scheduleNextPoll();

        // A realtime update landed while this request was in flight, so the
        // response may predate it and just overwrote it: fetch again.
        if (realtimeVersionRef.current !== versionAtStart) {
          setTimeout(() => {
            if (!isCancelled) fetchConversationData();
          }, 500);
        }
      } catch (error) {
        if (isCancelled) return;
        console.error("Failed to fetch conversations:", error);
        scheduleRetry();
      }
    };

    fetchConversationsRef.current = fetchConversationData;
    fetchConversationData();

    const handleVisibilityRefetch = () => {
      if (document.visibilityState === "visible") {
        fetchConversationData();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityRefetch);

    return () => {
      isCancelled = true;
      clearTimeout(pollTimeoutId);
      clearTimeout(retryTimeoutId);
      document.removeEventListener("visibilitychange", handleVisibilityRefetch);
    };
  }, [isLoggedIn, conversationFlag, conversationLimit]);

  const handleWSMsgs = (wsMsg) => {
    const __notif = new Audio(notifSound);
    if (
      wsMsg.type === "user-chat" ||
      wsMsg.type === "user-actions-chat" ||
      wsMsg.type === "user-thread-chat"
    ) {
      if (wsMsg.type === "user-thread-chat") {
        handleThreadChat(wsMsg.message);
      } else {
        const activeConversationId = activeConversationIdRef.current;
        if (wsMsg.message.conversation_id !== activeConversationId) {
          return;
        }

        const messageId = wsMsg.message.id;
        const alreadyInMessages = messagesDataRef.current?.messages?.some(
          (msg) => msg.id === messageId,
        );
        const alreadyInWs = wsMessagesDataRef.current?.some(
          (msg) => msg.id === messageId,
        );
        if (messageId && (alreadyInMessages || alreadyInWs)) {
          return;
        }

        setWsMessagesData((prevState) => {
          if (prevState.some((msg) => msg.id === messageId)) {
            return prevState;
          }
          return [...prevState, wsMsg.message];
        });
        websocketInstance.seenMessage(
          userDetails.id,
          userDetails.display_name,
          wsMsg.message.conversation_id,
          wsMsg.message.id,
        );
        if (userDetails.id !== wsMsg.message.sender_id) {
          // __notif.play().catch((err) => {});
        }

        if (wsMsg.message.action !== "DELETE") {
          if (isAtBottomRef.current) {
            setTimeout(() => scrollToBottom(), 100);
          }
        } else {
          handleMsgDeleted(wsMsg.message.chat_id);
        }
      }
    } else if (wsMsg.type === "user-reactions-chat") {
      const reactionType = wsMsg?.message?.type;
      if (reactionType === "created") {
        setWsReactionMessageId(wsMsg.message.id);
      } else {
        setWsDeletedReactionMessageId(wsMsg.message.id);
      }
      setWsReactionMessageDetail(wsMsg.message);
    }
  };

  const bumpTabUnreadCount = useCallback((isGroup, delta = 1) => {
    if (!delta) return;
    if (isGroup) {
      setGroupUnreadCount((prev) => Math.max(0, prev + delta));
    } else {
      setPersonalUnreadCount((prev) => Math.max(0, prev + delta));
    }
  }, []);

  const scheduleConversationListRefetch = useCallback(() => {
    clearTimeout(listRefetchDebounceRef.current);
    listRefetchDebounceRef.current = setTimeout(() => {
      if (typeof fetchConversationsRef.current === "function") {
        fetchConversationsRef.current();
      }
    }, 500);
  }, []);

  const clearConversationUnreadInList = useCallback(
    (conversationId) => {
      if (!conversationId) return;

      const prev = dataRef.current;
      if (!prev?.conversations) return;

      const idx = prev.conversations.findIndex(
        (conversation) => conversation.id === conversationId,
      );
      if (idx === -1) return;

      const existing = prev.conversations[idx];
      const previousUnread = existing.unread_count || 0;
      if (previousUnread <= 0) return;

      const conversations = [...prev.conversations];
      conversations[idx] = {
        ...existing,
        unread_count: 0,
      };
      commitData({ ...prev, conversations });
      bumpTabUnreadCount(!!existing.is_group, -previousUnread);
    },
    [bumpTabUnreadCount],
  );

  const handleConversationUpdated = useCallback(
    (payload) => {
      if (!payload || payload.type !== "conversation_updated") return;

      const {
        conversation_id,
        is_group,
        updatedAt,
        last_message,
        sender_id,
      } = payload;

      if (!conversation_id || !last_message) return;

      realtimeVersionRef.current += 1;

      const activeConversationId = activeConversationIdRef.current;
      const isActiveConversation = conversation_id === activeConversationId;
      const currentFlag = conversationFlagRef.current;
      const matchesCurrentTab =
        (currentFlag === "group" && !!is_group) ||
        (currentFlag === "personal" && !is_group);
      const shouldIncrementUnread =
        sender_id !== userDetails.id && !isActiveConversation;

      const notifyUnreadMessage = (conversationMeta = {}) => {
        if (!shouldIncrementUnread) return;

        const senderName =
          last_message?.sender?.display_name ||
          last_message?.user?.display_name ||
          "";
        const attachments =
          last_message?.message_attachment ||
          last_message?.attachments ||
          [];

        showDesktopNotification({
          senderName,
          conversationName: conversationMeta.name || "",
          content: last_message?.content || "",
          isGroup: !!is_group,
          hasAttachment: Array.isArray(attachments) && attachments.length > 0,
          icon:
            last_message?.sender?.profile_photo ||
            last_message?.user?.profile_photo ||
            conversationMeta.image ||
            "",
          conversationId: conversation_id,
          isDesktop: !isMobileMode,
        });
      };

      if (!matchesCurrentTab) {
        if (shouldIncrementUnread) {
          bumpTabUnreadCount(!!is_group, 1);
          notifyUnreadMessage();
        }
        return;
      }

      const prev = dataRef.current;
      const conversations = prev?.conversations || [];
      const idx = conversations.findIndex(
        (conversation) => conversation.id === conversation_id,
      );

      if (idx === -1) {
        scheduleConversationListRefetch();
        if (shouldIncrementUnread) {
          bumpTabUnreadCount(!!is_group, 1);
          notifyUnreadMessage();
        }
        return;
      }

      const existing = conversations[idx];
      const nextUnread = isActiveConversation
        ? 0
        : shouldIncrementUnread
          ? (existing.unread_count || 0) + 1
          : existing.unread_count || 0;

      const nextConversations = [...conversations];
      nextConversations[idx] = {
        ...existing,
        updatedAt: updatedAt || existing.updatedAt,
        last_message: {
          ...(existing.last_message || {}),
          ...last_message,
        },
        unread_count: nextUnread,
      };

      commitData({
        ...prev,
        conversations: nextConversations,
      });

      if (shouldIncrementUnread) {
        bumpTabUnreadCount(!!is_group, 1);
        notifyUnreadMessage({
          name: existing.name,
          image: existing.image,
        });
      }
    },
    [
      bumpTabUnreadCount,
      isMobileMode,
      scheduleConversationListRefetch,
      userDetails.id,
    ],
  );

  useEffect(() => {
    handleConversationUpdatedRef.current = handleConversationUpdated;
  }, [handleConversationUpdated]);

  const handleMsgDeleted = (chat_id) => {
    setWsDeleteActionsMessageId(chat_id);
  };

  const handleThreadChat = (chat_thread_detail) => {
    setWsThreadMsgs(chat_thread_detail);
  };

  const handleReceiveSeenMesasge = (data) => {
    setSeenMessageData(data);
  };

  const handleReceivePinMessage = (data) => {
    setPinMessageData(data);
  };

  const typingTimeoutRef = useRef(null); // Use ref to hold the timeout

  const clearTypingIndicator = useCallback(() => {
    clearTimeout(typingTimeoutRef.current);
    setShowingTyping(false);
    setTypingData(null);
  }, []);

  const isTypingForActiveConversation = (typing) => {
    if (!typing) return false;

    const typingRoomId =
      typing.room ?? typing.conversation_id ?? typing.conversationId;
    const activeConversationId = activeConversationIdRef.current;

    if (typingRoomId == null || activeConversationId == null) return false;

    return String(typingRoomId) === String(activeConversationId);
  };

  const setIsAtBottom = useCallback((val) => {
    isAtBottomRef.current = val;
  }, []);

  const applyMessagePatch = useCallback((messageId, patchFn) => {
    setMessagesData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        messages: prev.messages.map((msg) =>
          msg.id === messageId ? patchFn(msg) : msg
        ),
      };
    });
    setWsMessagesData((prev) =>
      prev.map((msg) => (msg.id === messageId ? patchFn(msg) : msg))
    );
  }, []);

  const connectWSRoom = (conversation_id) => {
    websocketInstance.connectRoom(conversation_id);
    clearConversationUnreadInList(conversation_id);
    if (!attachedListener) {
      websocketInstance.setupListeners((data) => {
        handleWSMsgs(data);
      });
      websocketInstance.seenMessage(
        userDetails.id,
        userDetails.display_name,
        conversation_id,
      );
      websocketInstance.receiveSeenMessage((data) => {
        handleReceiveSeenMesasge(data);
      });
      websocketInstance.receivePinMessage((data) => {
        handleReceivePinMessage(data);
      });
      websocketInstance.setupDisconnectListener();
      setAttachedListener(true);

      websocketInstance.currentlyTypingListener((typingData) => {
        if (!isTypingForActiveConversation(typingData)) {
          return;
        }

        setShowingTyping(true);
        setTypingData(typingData);

        clearTimeout(typingTimeoutRef.current);

        typingTimeoutRef.current = setTimeout(() => {
          setShowingTyping(false);
          setTypingData(null);
        }, 1000);
      });
    }
  };

  useEffect(() => {
    if (!isLoggedIn || !hasValidLinkConversation) {
      return;
    }

    connectWSRoom(linkConversation);
  }, [isLoggedIn, hasValidLinkConversation, linkConversation]);

  useEffect(() => {
    clearTypingIndicator();
  }, [storedConversationId, clearTypingIndicator]);

  useEffect(() => {
    websocketInstance.setConnectionStateListener(setWsConnectionState);

    return () => {
      websocketInstance.setConnectionStateListener(null);
    };
  }, [websocketInstance]);

  const syncMessagesAfterReconnect = useCallback(async ({ silent = false } = {}) => {
    if (!storedConversationId) return;

    const shouldStickToBottom =
      stickToBottomOnSyncRef.current || isAtBottomRef.current;

    // Only surface the "Restoring missed messages" banner if the sync is
    // actually slow; quick background syncs should be invisible.
    let bannerTimer;
    if (!silent) {
      bannerTimer = setTimeout(() => setIsRealtimeSyncing(true), 1500);
    }

    try {
      const result = await ApiService.message.getConversationMessages(
        storedConversationId,
        null,
        100,
      );
      const { success, messages = [] } = result || {};

      if (!success || messages.length === 0) {
        return;
      }

      setMessagesData((prevState) => {
        if (!prevState) return prevState;

        const mergedMessagesMap = new Map();

        for (const message of prevState.messages || []) {
          mergedMessagesMap.set(message.id, message);
        }

        for (const message of messages) {
          mergedMessagesMap.set(message.id, message);
        }

        const mergedMessages = Array.from(mergedMessagesMap.values()).sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );

        return {
          ...prevState,
          messages: mergedMessages,
        };
      });

      // The merge can add missed messages below and older ones above; keep
      // the thread at the bottom if that's where the user left it.
      if (shouldStickToBottom) pinToBottom();
    } catch (error) {
      console.error("Failed to sync messages after reconnect:", error);
    } finally {
      stickToBottomOnSyncRef.current = false;
      clearTimeout(bannerTimer);
      setIsRealtimeSyncing(false);
    }
  }, [storedConversationId]);

  useEffect(() => {
    const previousStatus = previousWsStatusRef.current;
    const currentStatus = wsConnectionState?.status;

    if (
      currentStatus === "connected" &&
      ["reconnecting", "disconnected", "failed"].includes(previousStatus)
    ) {
      // When the server recovered the session it already replayed missed
      // events, so the REST resync is only needed for a fresh session.
      if (!wsConnectionState?.recovered) {
        syncMessagesAfterReconnect();
        if (typeof fetchConversationsRef.current === "function") {
          fetchConversationsRef.current();
        }
      } else {
        stickToBottomOnSyncRef.current = false;
      }
      websocketInstance.initSidebarRooms();
    }

    previousWsStatusRef.current = currentStatus;
  }, [wsConnectionState?.status, syncMessagesAfterReconnect, websocketInstance]);

  // Coming back to the tab (e.g. a phone woken from sleep): reconnect if
  // needed and resync instead of reloading the whole page.
  const syncMessagesRef = useRef(null);
  useEffect(() => {
    syncMessagesRef.current = syncMessagesAfterReconnect;
  }, [syncMessagesAfterReconnect]);

  useEffect(() => {
    const BACKGROUND_SYNC_THRESHOLD_MS = 30 * 1000;
    let hiddenAt = null;
    // Captured on hide: scroll events fired while the tab is in the
    // background (throttled timers, stale layout) can flip isAtBottomRef.
    let wasAtBottomOnHide = true;

    const handleVisible = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        wasAtBottomOnHide = isAtBottomRef.current;
        return;
      }

      const hiddenFor = hiddenAt ? Date.now() - hiddenAt : 0;
      hiddenAt = null;

      // Content may have grown while hidden (live messages, late images).
      if (wasAtBottomOnHide) pinToBottom();

      const socket = websocketInstance.getConnection();
      if (socket && !socket.connected) {
        // reconnect -> status transition triggers the resync above
        stickToBottomOnSyncRef.current = wasAtBottomOnHide;
        socket.connect();
      } else if (
        hiddenFor > BACKGROUND_SYNC_THRESHOLD_MS &&
        typeof syncMessagesRef.current === "function"
      ) {
        // Socket stayed up, so live messages arrived normally; this is just a
        // quiet safety-net sync after a long absence (no banner).
        stickToBottomOnSyncRef.current = wasAtBottomOnHide;
        syncMessagesRef.current({ silent: true });
      }
    };

    document.addEventListener("visibilitychange", handleVisible);
    return () => document.removeEventListener("visibilitychange", handleVisible);
  }, [websocketInstance]);

  useEffect(() => {
    const handleOnline = () => {
      const socket = websocketInstance.getConnection();
      if (socket && !socket.connected) {
        socket.connect();
      }
    };

    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [websocketInstance]);

  const handleSetConversationData = (conversationDetail) => {
    const userDetails = localStorage.getItem("userDetails");
    const _userDetails = JSON.parse(userDetails);
    setConversationData(conversationDetail);
    const userPermissions = conversationDetail?.conversation.members.find(
      (conversationMember) => conversationMember.user_id === _userDetails.id,
    );
    setConversationUserPermission(userPermissions);
    setIsPermittedToChat(!userPermissions.is_muted);
    localStorage.setItem(
      "selectedConversationId",
      conversationDetail.conversation.id,
    );
  };

  const scrollToBottom = () => {
    const messageMain = messageMainRef.current;
    if (messageMain) {
      messageMain.scrollTo({
        top: messageMain.scrollHeight - messageMain.clientHeight,
        behavior: "auto",
      });
    }
  };

  // Opening a conversation: keep the thread pinned to the bottom while its
  // content is still growing (images/avatars loading, layout settling, the
  // loading overlay going away). A single scroll right after render lands in
  // the middle because the height keeps increasing afterwards. Stops after
  // `durationMs` or as soon as the user scrolls/touches the thread.
  const pinToBottomCleanupRef = useRef(null);
  const pinToBottom = useCallback((durationMs = 2500) => {
    if (pinToBottomCleanupRef.current) pinToBottomCleanupRef.current();

    const USER_EVENTS = ["wheel", "touchstart", "keydown", "mousedown"];
    let el = null;
    let observer = null;
    let timer = null;
    let raf = null;
    let stopped = false;

    const toBottom = () => {
      if (el) el.scrollTop = el.scrollHeight;
    };

    const stop = () => {
      if (stopped) return;
      stopped = true;
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      if (observer) observer.disconnect();
      if (el) {
        el.removeEventListener("load", toBottom, true);
        USER_EVENTS.forEach((evt) => el.removeEventListener(evt, stop));
      }
      pinToBottomCleanupRef.current = null;
    };

    const attach = (tries = 0) => {
      if (stopped) return;
      el = messageMainRef.current;
      if (!el) {
        // The thread pane may not be mounted yet on the very first open.
        if (tries < 30) raf = requestAnimationFrame(() => attach(tries + 1));
        return;
      }

      toBottom();
      if (typeof ResizeObserver !== "undefined") {
        observer = new ResizeObserver(toBottom);
        observer.observe(el);
        if (el.firstElementChild) observer.observe(el.firstElementChild);
      }
      el.addEventListener("load", toBottom, true);
      USER_EVENTS.forEach((evt) =>
        el.addEventListener(evt, stop, { passive: true }),
      );
      timer = setTimeout(stop, durationMs);
    };

    pinToBottomCleanupRef.current = stop;
    attach();
  }, []);

  useEffect(
    () => () => {
      if (pinToBottomCleanupRef.current) pinToBottomCleanupRef.current();
    },
    [],
  );

  const getCachedConversationPayload = useCallback((conversationId) => {
    const cachedPayload = conversationCacheRef.current.get(conversationId);

    if (!cachedPayload) return null;

    const isStale = Date.now() - cachedPayload.fetchedAt > CONVERSATION_CACHE_TTL_MS;
    if (isStale) {
      conversationCacheRef.current.delete(conversationId);
      return null;
    }

    return cachedPayload;
  }, []);

  const cacheConversationPayload = useCallback(
    (conversationId, conversationDetail, messagesDetail) => {
      conversationCacheRef.current.set(conversationId, {
        conversationDetail,
        messagesDetail,
        fetchedAt: Date.now(),
      });
    },
    [],
  );

  const applyConversationPayload = useCallback(
    (
      conversationId,
      conversationDetail,
      messagesDetail,
      messageLimit = 30,
    ) => {
      setConversationId(conversationId);
      handleSetConversationData(conversationDetail);
      setConversationMembers(conversationDetail.conversation.members);
      setMessagesData(messagesDetail);
      setHasMoreMessages((messagesDetail.messages?.length ?? 0) >= messageLimit);
      setHasSelectedChat(true);
      setChatReplyData(null);
      setWsMessagesData([]);

      if (isMobileMode) {
        setMobileChatDetailShow(true);
      } else {
        setMobileChatDetailShow(false);
      }
    },
    [isMobileMode],
  );

  const setConversationDetail = async (conversationId) => {
    activeConversationIdRef.current = conversationId;
    setConversationId(conversationId);
    setWsMessagesData([]);

    const cachedPayload = getCachedConversationPayload(conversationId);
    const hasCachedPayload = Boolean(cachedPayload);

    if (!hasCachedPayload) {
      setMessagesData(null);
      setConversationData(null);
      setLoading(true);
      // On mobile, open the chat pane immediately so the in-pane spinner is visible
      // while messages load (otherwise chat__main stays collapsed until applyConversationPayload).
      if (isMobileMode) {
        setMobileChatDetailShow(true);
        setHasSelectedChat(true);
      }
    } else {
      applyConversationPayload(
        conversationId,
        cachedPayload.conversationDetail,
        cachedPayload.messagesDetail,
      );
      setLoading(false);
      pinToBottom();
    }

    try {
      const [conversationDetail, messagesDetail] = await Promise.all([
        ApiService.v2.conversations.getConversationDetailById(conversationId),
        ApiService.message.getConversationMessages(conversationId, null, 30),
      ]);

      if (activeConversationIdRef.current !== conversationId) return;

      const { success: conversationSuccess } = conversationDetail;
      const { success: messagesSuccess } = messagesDetail;

      if (!conversationSuccess || !messagesSuccess) {
        if (!hasCachedPayload) {
          setLoading(false);
          showSwal(common.errors.SET_CONVERSATION_DATA_ERROR).then(() => {
            window.location.reload();
          });
        }
        return;
      }

      cacheConversationPayload(conversationId, conversationDetail, messagesDetail);
      applyConversationPayload(conversationId, conversationDetail, messagesDetail);

      pinToBottom();
    } catch (error) {
      if (activeConversationIdRef.current !== conversationId) return;
      if (!hasCachedPayload) {
        setError(error);
      }
    } finally {
      if (activeConversationIdRef.current === conversationId) {
        if (!hasCachedPayload) {
          setTimeout(() => setLoading(false), 800);
        }
      }
    }
  };

  const reSetConversationDetail = async (
    conversationId,
    limit,
    callback,
  ) => {
    activeConversationIdRef.current = conversationId;
    setConversationId(conversationId);
    setWsMessagesData([]);
    setMessagesData(null);
    setConversationData(null);
    setLoading(true);
    if (isMobileMode) {
      setMobileChatDetailShow(true);
      setHasSelectedChat(true);
    }
    try {
      const conversationDetail =
        await ApiService.v2.conversations.getConversationDetailById(
          conversationId,
        );
      const messagesDetail = await ApiService.message.getConversationMessages(
        conversationId,
        null,
        limit,
      );

      if (activeConversationIdRef.current !== conversationId) return;

      const { success: conversationSuccess } = conversationDetail;
      const { success: messagesSuccess } = messagesDetail;

      if (!conversationSuccess || !messagesSuccess) {
        setLoading(false);
        showSwal(common.errors.SET_CONVERSATION_DATA_ERROR).then(() => {
          window.location.reload();
        });
        return;
      }
      cacheConversationPayload(conversationId, conversationDetail, messagesDetail);
      applyConversationPayload(
        conversationId,
        conversationDetail,
        messagesDetail,
        limit,
      );
      setLoading(false);
      callback(true);
    } catch (error) {
      setLoading(false);
      if (activeConversationIdRef.current !== conversationId) return;
      console.error(error);
      setError(error);
    }
  };

  const updateConversationData = async (
    conversationId,
    isViewMoreClicked,
    oldScrollHeight,
  ) => {
    // No conversation selected: nothing to fetch (would request /messages/null).
    if (isViewMoreClicked ? !storedConversationId : !conversationId) {
      return Promise.resolve();
    }
    if (isViewMoreClicked && isLoadingMoreRef.current) return Promise.resolve();
    if (isViewMoreClicked) isLoadingMoreRef.current = true;

    try {
      setLoading(true);
      let conversationDetail, messagesDetail;
      if (isViewMoreClicked) {
        return new Promise((resolve) => {
          const messageMain = document.querySelector(".message__main");
          const savedScrollHeight = messageMain.scrollHeight;
          const savedScrollTop = messageMain.scrollTop;

          // Use the ID of the oldest currently loaded message as the cursor.
          // messagesData.messages is newest-first from the API, so the last
          // element is the oldest loaded message.
          const currentMessages = messagesData?.messages ?? [];
          const cursor = currentMessages[currentMessages.length - 1]?.createdAt ?? null;

          ApiService.message.getConversationMessages(
            storedConversationId,
            cursor,
            10,
          ).then((result) => {
            const incoming = result?.messages ?? [];

            // Append older messages after existing ones (both arrays are newest-first,
            // so appending keeps the array newest-first overall).
            setMessagesData((prev) => {
              const existing = prev?.messages ?? [];
              const existingIds = new Set(existing.map((m) => m.id));
              return {
                ...prev,
                messages: [
                  ...existing,
                  ...incoming.filter((m) => !existingIds.has(m.id)),
                ],
              };
            });
            setHasMoreMessages(incoming.length >= 10);

            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                const newScrollHeight = messageMain.scrollHeight;
                messageMain.scrollTop = newScrollHeight - savedScrollHeight + savedScrollTop;
                setLoading(false);
                isLoadingMoreRef.current = false;
                resolve();
              });
            });
          });
        });
      } else {
        conversationDetail =
          await ApiService.v2.conversations.getConversationDetailById(
            conversationId,
          );
        messagesDetail = await ApiService.message.getConversationMessages(
          conversationId,
        );
        const { success: conversationSuccess } = conversationDetail;
        const { success: messagesSuccess } = messagesDetail;
        if (!conversationSuccess || !messagesSuccess) {
          setLoading(false);
          showSwal(common.errors.UPDATE_CONVERSATION_DATA_ERROR).then(() => {
            window.location.reload();
          });
        }
        setConversationId(conversationId);
        handleSetConversationData(conversationDetail);
        setConversationMembers(conversationDetail.conversation.members);
        setMessagesData(messagesDetail);
        setHasMoreMessages((messagesDetail.messages?.length ?? 0) >= 30);
        setHasSelectedChat(true);
        setChatReplyData(null);
        setWsMessagesData([]);
        isMobileMode
          ? setMobileChatDetailShow(true)
          : setMobileChatDetailShow(false);
        setLoading(false);
      }
    } catch (error) {
      isLoadingMoreRef.current = false;
      setError(error);
    }
  };

  const showHideChangePasswordModal = () => {
    setViewChangePasswordModal(!viewChangePasswordModal);
  };

  // WS mutation effects — apply incoming WS events to message state immutably.
  // These replace the direct context-object mutations that were previously in main.jsx.

  useEffect(() => {
    if (!wsDeleteActionsMessageId) return;
    applyMessagePatch(wsDeleteActionsMessageId, (msg) => ({ ...msg, is_deleted: true }));
  }, [wsDeleteActionsMessageId]);

  useEffect(() => {
    if (!wsTheradMsgs?.reply_to_thread_message_id) return;
    applyMessagePatch(wsTheradMsgs.reply_to_thread_message_id, (msg) => ({
      ...msg,
      thread_replies: [...(msg.thread_replies ?? []), wsTheradMsgs],
    }));
  }, [wsTheradMsgs]);

  useEffect(() => {
    if (!wsReactionMessageId || !wsReactionMessageDetail) return;
    applyMessagePatch(wsReactionMessageDetail.message_id, (msg) => ({
      ...msg,
      message_reactions: [...msg.message_reactions, wsReactionMessageDetail],
    }));
  }, [wsReactionMessageId]);

  useEffect(() => {
    if (!wsDeletedReactionMessageId || wsReactionMessageDetail?.type !== "removed") return;
    applyMessagePatch(wsReactionMessageDetail.message_id, (msg) => ({
      ...msg,
      message_reactions: msg.message_reactions.filter(
        (r) => r.id !== wsReactionMessageDetail.id
      ),
    }));
  }, [wsDeletedReactionMessageId]);

  useEffect(() => {
    if (!seenMessageData?.user_id) return;
    const { user_id, display_name, message_id } = seenMessageData;
    const applySeenPatch = (msg) => {
      const reads = (msg.message_reads ?? []).filter((r) => r?.user_id !== user_id);
      if (msg.id === message_id) {
        reads.push({
          id: "-WS",
          user: { id: user_id, display_name: display_name ?? "Unknown" },
          user_id,
        });
      }
      return { ...msg, message_reads: reads };
    };
    setMessagesData((prev) => {
      if (!prev) return prev;
      return { ...prev, messages: prev.messages.map(applySeenPatch) };
    });
    setWsMessagesData((prev) => prev.map(applySeenPatch));
  }, [seenMessageData]);

  useEffect(() => {
    if (!pinMessageData) return;
    setConversationData((prev) => {
      if (!prev?.conversation) return prev;
      return {
        ...prev,
        conversation: {
          ...prev.conversation,
          pinned_message: pinMessageData.pin_data ?? null,
        },
      };
    });
    if (!pinMessageData.pin_data?.id) return;
    const pinId = pinMessageData.pin_data.id;
    const pinValue = pinMessageData.pin_data.is_pinned;
    const applyPinPatch = (msg) => ({
      ...msg,
      is_pinned: msg.id === pinId ? pinValue : false,
    });
    setMessagesData((prev) => {
      if (!prev) return prev;
      return { ...prev, messages: prev.messages.map(applyPinPatch) };
    });
    setWsMessagesData((prev) => prev.map(applyPinPatch));
  }, [pinMessageData]);

  const handleThreadViewMsgId = (id, conversation_id = null) => {
    setThreadViewMsgId(id);
    fetchThreadDetails(id, conversation_id);
  };

  const handleThreadViewVisible = () => {
    setThreadViewVisible(!threadViewVisible);
  };
  const handleReactionsViewVisible = (reactions = []) => {
    setReactionViewList(reactions);
    setReactionsViewVisible(!reactionsViewVisible);
  };

  const fetchThreadDetails = async (message_id, conversation_id = null) => {
    setLoading(true);
    const finalConversationId = conversation_id
      ? conversation_id
      : storedConversationId;
    const threadDetails = await ApiService.message.getThreadMessages(
      finalConversationId,
      message_id,
    );
    const { success, thread_detail } = threadDetails;
    if (!success) {
      setLoading(false);
      showSwal(common.errors.FETCH_THREAD_ERROR);
    }
    setThreadViewDetails(thread_detail);
    setStoredThreadMessageId(message_id);
    handleThreadViewVisible(true);
    setLoading(false);
  };
  const reFetchThreadDetails = async (message_id) => {
    const threadDetails = await ApiService.message.getThreadMessages(
      storedConversationId,
      message_id,
    );
    const { success, thread_detail } = threadDetails;
    if (!success) {
      showSwal(common.errors.FETCH_THREAD_ERROR);
    }
    setThreadViewDetails(thread_detail);
    setLoading(false);
  };

  useEffect(() => {
    if (isLoggedIn && storedThreadMessageId && threadViewVisible) {
      // const intervalId = setInterval(() => reFetchThreadDetails(storedThreadMessageId), 5000);
      const intervalId = setInterval(
        () => reFetchThreadDetails(storedThreadMessageId),
        45 * 1000,
      );
      return () => clearInterval(intervalId);
    }
  }, [isLoggedIn, storedThreadMessageId, threadViewVisible]);

  const chatContextValue = useMemo(() => ({
    setConversationDetail,
    storedConversationId,
    conversationData,
    conversationUserPermission,
    isPermittedToChat,
    conversationMembersData,
    messagesData,
    hasSelectedChat,
    setChatReplyData,
    chatReplyData,
    isMobileChatDetailShow,
    setMobileMode,
    messageMainRef,
    updateConversationData,
    hasMoreMessages,
    setMessagesData,
    setMobileChatDetailShow,
    isMobileMode,
    scrollToBottom,
    wsMessagesData,
    wsDeleteActionsMessageId,
    handleThreadViewMsgId,
    threadViewMsgId,
    handleThreadViewVisible,
    threadViewVisible,
    threadViewDetails,
    reFetchThreadDetails,
    wsTheradMsgs,
    reSetConversationDetail,
    wsReactionMessageId,
    wsReactionMessageDetail,
    wsDeletedReactionMessageId,
    setWsReactionMessageId,
    isLoggedIn,
    isLoading: loading,
    onlineUsers,
    isShowingTyping,
    typingData,
    seenMessageData,
    wsConnectionState,
    isRealtimeSyncing,
    setConversationFlag,
    pinMessageData,
    setConversationLimit,
    handleReactionsViewVisible,
    reactionsViewVisible,
    reactionViewList,
    setIsAtBottom,
    personalUnreadCount,
    groupUnreadCount,
    setPersonalUnreadCount,
    setGroupUnreadCount,
  }), [
    storedConversationId, conversationData, conversationUserPermission,
    isPermittedToChat, conversationMembersData, messagesData, hasSelectedChat,
    chatReplyData, isMobileChatDetailShow, isMobileMode, hasMoreMessages,
    wsMessagesData, wsDeleteActionsMessageId, threadViewMsgId, threadViewVisible,
    threadViewDetails, wsTheradMsgs, wsReactionMessageId, wsReactionMessageDetail,
    wsDeletedReactionMessageId, isLoggedIn, loading, onlineUsers, isShowingTyping,
    typingData, seenMessageData, wsConnectionState, isRealtimeSyncing,
    pinMessageData, reactionsViewVisible, reactionViewList,
    personalUnreadCount, groupUnreadCount,
  ]);

  return (
    <>
      <WebSocketContext.Provider
        value={{
          websocketInstance: websocketInstance,
          connectWSRoom: connectWSRoom,
        }}
      >
        <ChatContext.Provider value={chatContextValue}>
          {/* <Header
            userDetails={userDetails}
            showHideChangePasswordModal={showHideChangePasswordModal}
            viewChangePasswordModal={viewChangePasswordModal}
          /> */}
          <Main
            conversations={data?.conversations}
            conversationsCount={data?.pagination?.total}
            setConversationLimit={setConversationLimit}
            conversationLimit={conversationLimit}
            userDetails={userDetails}
            linkConversation={linkConversation}
            showHideChangePasswordModal={showHideChangePasswordModal}
            viewChangePasswordModal={viewChangePasswordModal}
          />
        </ChatContext.Provider>
      </WebSocketContext.Provider>
    </>
  );
}

export default ChatIndex;
